import type { GetServerSideProps } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import nextI18nextConfig from 'next-i18next.config'
import { DependencyInjector } from '~/injector/dependency.injector'
import { HtmlPageMetaContextService } from '~/components/HtmlPageMeta/HtmlPageMetaContextService'
import { OrganizerRepositoryInterface } from '~/modules/Organizers/Domain/OrganizerRepositoryInterface'
import GetFeaturedOrganizers from '~/modules/Organizers/Application/GetFeaturedOrganizers/GetFeaturedOrganizers'
import { HtmlPageMetaContextProps } from '~/components/HtmlPageMeta/HtmlPageMetaContextProps'
import GetFeaturedOrganizersResponseInterface
  from '~/modules/Organizers/Application/GetFeaturedOrganizers/GetFeaturedOrganizersResponseInterface'
import { NextPage } from 'next'
import { MarketHomePage } from '~/components/MarketPages/HomePage'
import { fromHomePagePropsToViewModel } from '~/modules/PresentationAdapters/HomePageAdapter'
import { TrophyRepositoryInterface } from '~/modules/Trophies/Domain/TrophyRepositoryInterface'
import { ApplicationTrophyDto } from '~/modules/Products/Application/Dtos/ApplicationTrophyDto'
import { ApplicationTrophyDtoTransformer } from '~/modules/Products/Application/ApplicationTrophyDtoTransformer'
import { BigNumber } from 'ethers'

export interface HomePageProps {
  htmlPageMetaContextProps: HtmlPageMetaContextProps
  organizers: GetFeaturedOrganizersResponseInterface[]
  featuredAwards: ApplicationTrophyDto[]
  page: {
    number: number
    size: number
  }
}

const fallbackFeaturedAwards: ApplicationTrophyDto[] = [
  {
    id: '38089',
    type: 'trophy',
    animation: null,
    discipline: 'Baseball',
    imageUrl: 'https://cdn.leverade.com/trophyverse/trophies/wbsc-baseball-gold-2024.jpg',
    content: {
      header: 'Blind Baseball International Cup',
      subtitle: 'Gold Medal | September 2024',
      title: 'Maikel Rodríguez Cedeño',
    },
    memoriesCount: 0,
    mintStatus: 'pending',
    createdAt: null,
    description: "Maikel Rodríguez Cedeño's Gold Medal won in Blind Baseball International Cup.",
  },
  {
    id: '37819',
    type: 'trophy',
    animation: null,
    discipline: 'Gimnastic - Artistic',
    imageUrl: 'https://cdn.leverade.com/trophyverse/trophies/panamerican-gold-2023.jpg',
    content: {
      header: 'Pan American Games',
      subtitle: 'Pan American Games Champion and Gold Medalist | October 2023',
      title: 'Rodrigues De Andrade Rebeca',
    },
    memoriesCount: 0,
    mintStatus: 'pending',
    createdAt: null,
    description: 'Pan American Games Champion and Gold Medalist certificate.',
  },
  {
    id: '38472',
    type: 'trophy',
    animation: null,
    discipline: 'Archery',
    imageUrl: 'https://cdn.leverade.com/trophyverse/trophies/asu-gold-2025.jpg',
    content: {
      header: 'Junior Pan American Games',
      subtitle: 'Pan American Games Champion and Gold Medalist | August 2025',
      title: 'Pereira Dos Santos Miguel',
    },
    memoriesCount: 0,
    mintStatus: 'pending',
    createdAt: null,
    description: 'Junior Pan American Games Champion and Gold Medalist medal.',
  },
]

export const getServerSideProps: GetServerSideProps = async (context) => {
  const pageNumber = 1
  const pageSize = 4
  const croatianFootballFederationId = 'f7a9c3c2-bfab-4667-b5f4-f98491a84ce3'
  const locale = context.locale ? context.locale : nextI18nextConfig.i18n.defaultLocale
  const translationsPromise = serverSideTranslations(
    locale,
    [
      'user_tos',
      'app_menu',
      'faq',
      'featured_banner',
      'featured_galleries',
      'featured_organizers',
      'featured_awards',
      'footer',
      'home',
      'trophies',
      'wallet_error_modal',
    ]
  )

  const organizerRepository: OrganizerRepositoryInterface = DependencyInjector.getInstance()
    .resolve('OrganizerRepositoryInterface')
  const trophyRepository: TrophyRepositoryInterface = DependencyInjector.getInstance()
    .resolve('TrophyRepositoryInterface')
  const getFeaturedOrganizersUseCase = new GetFeaturedOrganizers(organizerRepository)
  const featuredAwardIds = ['38089', '37819', '38472']
  const shouldUseFallbackFeaturedAwards = process.env.NODE_ENV !== 'production'
  let featuredOrganizers: GetFeaturedOrganizersResponseInterface[] = []
  let featuredAwards: ApplicationTrophyDto[] = shouldUseFallbackFeaturedAwards ? fallbackFeaturedAwards : []

  try {
    featuredOrganizers = (await getFeaturedOrganizersUseCase.run()).filter(
      (organizer) => organizer.id !== croatianFootballFederationId
    )
  } catch (exception: unknown) {
    console.error('Failed to load featured organizers for home page', exception)
  }

  try {
    const trophies = await Promise.all(
      featuredAwardIds.map(async (trophyId) => trophyRepository.findById(BigNumber.from(trophyId), ['organizer']))
    )

    const loadedFeaturedAwards = trophies.flatMap((trophy) => {
      return trophy === null ? [] : [ApplicationTrophyDtoTransformer.fromDomain(trophy)]
    })

    if (loadedFeaturedAwards.length === featuredAwardIds.length) {
      featuredAwards = loadedFeaturedAwards
    } else if (shouldUseFallbackFeaturedAwards) {
      featuredAwards = featuredAwardIds.map((featuredAwardId) => {
        return loadedFeaturedAwards.find((award) => award.id === featuredAwardId) ??
          fallbackFeaturedAwards.find((award) => award.id === featuredAwardId)
      }).filter((award): award is ApplicationTrophyDto => award !== undefined)
    } else {
      featuredAwards = loadedFeaturedAwards
    }
  } catch (exception: unknown) {
    console.error('Failed to load featured awards for home page', exception)
  }

  const htmlPageMetaContextService = new HtmlPageMetaContextService(context)

  return {
    props: {
      // Serialize the SSR payload into plain JSON-safe data for Next.js.
      organizers: JSON.parse(JSON.stringify(featuredOrganizers)),
      featuredAwards: JSON.parse(JSON.stringify(featuredAwards)),
      page: { number: pageNumber, size: pageSize },
      htmlPageMetaContextProps: htmlPageMetaContextService.getProperties(),
      ...await translationsPromise,
    },
  }
}

const HomeRoutePage: NextPage<HomePageProps> = (props) => {
  return <MarketHomePage viewModel={ fromHomePagePropsToViewModel(props) } />
}

export default HomeRoutePage
